import localhost from "apis/localhost";
import {
  addToJobQueue,
  clearJobQueue,
  removeFromJobQueue,
} from "slices/app/appSlice";
import { showErrorModal } from "slices/app/actions/error";
import { populateErrorMessage } from "slices/app/errorSlice";
import { renameKeys } from "utils";
import {
  addIgnoredDocument,
  canLoadContent,
  check,
  expandTheme,
  filterThemes,
  removeIgnoredDocument,
  setCurrentUniqueItemCount,
  setDisableSaveSnapshot,
  setSearch,
  setSearchTerm,
  setThemes,
  setThemesItems,
  setUniqueItemCount,
  showAllThemesAndItems,
} from "slices/discovery/discoverySlice";
import {
  applyFilter,
  clearSearchAndFilterState,
} from "slices/discovery/actions/filters";
import {
  displayNoFilterResults,
  displayNoSearchResults,
} from "components/Modal/confModalTypes";
import { getSelectedDocumentIds } from "slices/discovery/helpers/theme";
import { applySnapshot } from "slices/discovery/actions/snapshot";
import { FILTER_TYPES } from "constants/discovery";
import { getCheckedThemes } from "layout/discovery/helpers";

const ALL_THEMES = "All Themes";
const TERMS_URL = "/docs/discovery/terms";
const pollInterval = 3000;
export const finalKeys = {
  id: "Id",
  fq: "Occurrences",
  nme: "Name",
  bc: "BackgroundColour",
  tc: "TextColour",
  cNme: "GroupName",
  docs: "Documents",
  items: "NounPhrases",
  iNme: "NounPhrase",
  iNmeEsc: "NounPhraseEscaped",
  fbs: "FilteredBySearch",
};

export const getNounPhrases = (data, groupName) => {
  let nounPhrases = [];
  if (data.NounPhrases) {
    return data.NounPhrases.map((nounPhrase) => ({
      ...nounPhrase,
      GroupName: groupName,
    }));
  }
  return nounPhrases;
};

export const prepareThemesOriginalCase = (data, groupNames) => {
  const groupNameMap = groupNames.reduce((acc, name) => {
    acc[name.toLowerCase()] = name;
    return acc;
  }, {});

  return data.map((theme) => {
    const groupNameOriginalCase =
      groupNameMap[theme.GroupName.toLowerCase()] || theme.GroupName;

    return {
      ...theme,
      GroupName: groupNameOriginalCase,
      NounPhrases: getNounPhrases(theme, groupNameOriginalCase),
    };
  });
};

// Split this action into two different actions: first one for total count and second one for current count
export const fetchUniqueItemsCount =
  (filterType, documentId, newSearchTerm, docToIgnoreId, fetchBaseCount) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("fetchUniqueItemsCount"));
    const { discovery } = getState();

    const {
      config: { searchTerm: prevSearchTerm, filterDocID },
      uniqueItemCount,
    } = getState().discovery;
    const searchTerm = newSearchTerm ? newSearchTerm : prevSearchTerm;

    let documentIds = [];
    if (filterType === FILTER_TYPES.GAPS_RELATIVE_TO_A_REFERENCE_DOC) {
      if (documentId) {
        documentIds = [documentId];
      } else {
        documentIds = [filterDocID];
      }
    } else {
      documentIds = getSelectedDocumentIds(getState().discovery);
    }

    if (docToIgnoreId !== null) {
      documentIds = documentIds.filter((docId) => docId !== docToIgnoreId);
    }

    const params = {
      docSetId: discovery.config.folderId,
      documentIds: documentIds,
      discoveryFilter: filterType !== null ? filterType : undefined,
      searchTerm: searchTerm !== "" ? searchTerm : undefined,
    };
    if (fetchBaseCount) {
      params.discoveryFilter = undefined;
      params.searchTerm = undefined;
    }

    await localhost
      .post("/docs/discovery/uniqueItemCount", params)
      .then((response) => {
        const { data } = response;
        if (data === 0) return;
        if (uniqueItemCount === 0 || fetchBaseCount) {
          dispatch(setUniqueItemCount(data));
        } else {
          dispatch(setCurrentUniqueItemCount(data));
        }
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(removeFromJobQueue("fetchUniqueItemsCount"));
      });
  };

export const fetchThemes = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("fetchThemes"));
  const params = {
    docSetId: getState().discovery.config.folderId,
    documentId: getState().discovery.config.documentId,
  };

  await localhost
    .get("/docs/discovery/groups", {
      params,
    })
    .then((response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      dispatch(setThemes(data));
      dispatch(
        fetchUniqueItemsCount(undefined, undefined, undefined, undefined, true)
      );
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(removeFromJobQueue("fetchThemes"));
    });
};

export const fetchItems = (snapshot) => async (dispatch, getState) => {
  if (!snapshot.expandedGroups) return;

  dispatch(addToJobQueue("genericJob"));

  const { discovery } = getState();
  const groupNames = snapshot.expandedGroups.map((theme) => theme.groupName);
  const params = {
    docSetId: discovery.config.folderId,
    documentIds: snapshot.checkedDocuments,
    groupNames,
  };

  await localhost
    .post(TERMS_URL, params)
    .then(async (response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      const items = prepareThemesOriginalCase(data, groupNames);
      await dispatch(setThemesItems(items));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    });

  dispatch(clearJobQueue());
};

export const expand = (theme, snapshot) => async (dispatch, getState) => {
  const { discovery } = getState();

  // If items are already in the state don't fetch them
  if (
    !discovery.config.filterView &&
    discovery.themes.filter((t) => t.GroupName === theme.GroupName).length > 1
  ) {
    dispatch(expandTheme(theme));
    return;
  }

  const params = {
    docSetId: discovery.config.folderId,
    documentIds: getSelectedDocumentIds(discovery),
    groupNames: [theme.GroupName],
    discoveryFilter: discovery.config.filterType,
    searchTerm: discovery.config.searchTerm,
  };

  await localhost
    .post(TERMS_URL, params)
    .then(async (response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      const themeToExpand = {
        ...data[0],
        GroupName: theme.GroupName,
        NounPhrases: data[0].NounPhrases.map((nounPhrase) => ({
          ...nounPhrase,
          GroupName: theme.GroupName,
          collapsed: false,
          FilteredByFilter: discovery.config.filterView,
        })),
      };

      if (discovery.config.filterView && snapshot?.expandedGroups.length) {
        const checkedNPs = new Set();

        snapshot.expandedGroups.forEach((group) => {
          group?.checkedNounPhrases.forEach((np) => {
            checkedNPs.add(np);
          });
        });

        themeToExpand.NounPhrases = themeToExpand.NounPhrases.map((np) => {
          return {
            ...np,
            checked: checkedNPs.has(np.NounPhrase),
          };
        });
      }

      dispatch(expandTheme(themeToExpand));

      if (discovery.config.filterView && snapshot?.showAll === false) {
        dispatch(showAllThemesAndItems(false));
      }
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    });
};

export const addChecked = (theme) => async (dispatch, getState) => {
  dispatch(canLoadContent(true));

  const { discovery } = getState();
  const params = {
    docSetId: getState().discovery.config.folderId,
    documentIds: getSelectedDocumentIds(discovery),
    groupNames: [theme.GroupName],
    discoveryFilter: discovery.config.filterType,
    searchTerm: discovery.config.searchTerm,
  };

  // If items are already in the state don't fetch them, or we checked a noun phrase
  if (
    (!discovery.config.filterView &&
      discovery.themes.filter((t) => t.GroupName === theme.GroupName).length >
        1) ||
    theme.NounPhrase !== undefined
  ) {
    dispatch(check(theme));
    return;
  }

  await localhost
    .post(TERMS_URL, params)
    .then((response) => {
      let { data } = response;
      if (data.length) {
        data = renameKeys(data, finalKeys);
        const themeToCheck = {
          ...data[0],
          GroupName: theme.GroupName,
          checked: true,
          NounPhrases: data[0].NounPhrases.map((nounPhrase) => ({
            ...nounPhrase,
            checked: true,
            GroupName: theme.GroupName,
            collapsed: false,
            FilteredByFilter: discovery.config.filterView,
          })),
        };
        dispatch(check(themeToCheck));
      } else {
        dispatch(displayNoFilterResults(discovery.config.filterType));
      }
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    });
};

export const checkAllForSearch = (themes) => (dispatch) => {
  themes
    .filter((element) => element.NounPhrase)
    .forEach((item) => {
      if (item.FilteredBySearch) {
        dispatch(addChecked(item));
      }
    });
};

export const filterItems =
  (searchTerm, docToIgnoreId, snapshot) => async (dispatch, getState) => {
    dispatch(addToJobQueue("themeFilterItems"));

    const { discovery } = getState();
    const groupNames = discovery.themes
      .filter(
        (theme) =>
          theme.GroupName && !theme.NounPhrase && theme.GroupName !== ALL_THEMES
      )
      .map((theme) => theme.GroupName);

    let documentIds;
    if (snapshot) {
      documentIds = snapshot.checkedDocuments;
    } else {
      documentIds = getSelectedDocumentIds(discovery);
    }

    if (docToIgnoreId !== null) {
      documentIds = documentIds.filter((docId) => docId !== docToIgnoreId);
    }

    const params = {
      docSetId: discovery.config.folderId,
      documentIds,
      groupNames,
      searchTerm,
    };

    await localhost
      .post(TERMS_URL, params)
      .then(async (response) => {
        let { data } = response;
        if (data.length) {
          // clear state
          await dispatch(clearSearchAndFilterState(!snapshot));
          dispatch(addToJobQueue("themeFilterItems"));
          dispatch(setSearch(true));
          dispatch(setSearchTerm(searchTerm));

          // apply new data
          data = renameKeys(data, finalKeys);
          const themes = prepareThemesOriginalCase(data, groupNames);
          dispatch(filterThemes(themes));
          dispatch(setDisableSaveSnapshot(false));
          if (docToIgnoreId !== null) {
            dispatch(addIgnoredDocument(docToIgnoreId));
          }
          if (snapshot) {
            dispatch(applySnapshot(snapshot));
          }
        } else {
          dispatch(displayNoSearchResults(searchTerm));
        }
      })
      .catch((error) => {
        dispatch(setSearch(false));
        dispatch(setSearchTerm(""));
        dispatch(showErrorModal(error));
      });

    dispatch(removeFromJobQueue("themeFilterItems"));
  };

export const searchItems =
  (searchTerm, docToIgnoreId) => async (dispatch, getState) => {
    const { filterView, filterType, filterDocID } = getState().discovery.config;
    if (filterView) {
      dispatch(setSearch(false));
      dispatch(applyFilter(filterType, filterDocID, searchTerm, docToIgnoreId));
    } else {
      dispatch(filterItems(searchTerm, docToIgnoreId, null));
    }
    dispatch(
      fetchUniqueItemsCount(filterType, null, searchTerm, docToIgnoreId)
    );
  };

export const download = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", "true");
  a.click();
};

export const pollJobStatus = (jobId) => async (dispatch) => {
  await localhost
    .get("docs/jobStatus", {
      params: { jobId },
    })
    .then((jobResp) => {
      if (jobResp.data && jobResp.data.status === "RUNNING") {
        setTimeout(() => {
          dispatch(pollJobStatus(jobId));
        }, pollInterval);
      } else if (jobResp.data && jobResp.data.status === "ERROR") {
        dispatch(populateErrorMessage("Failed to generate Excel report"));
        dispatch(clearJobQueue());
      } else {
        download(jobResp.data.jobMetaInfo.file);
        dispatch(clearJobQueue());
      }
    })
    .catch((error) => {
      dispatch(populateErrorMessage(error));
      dispatch(clearJobQueue());
    });
};

export const exportAsCsv = (folderId, docs) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));
  const {
    filterType: discoveryFilter,
    searchTerm,
    showAll,
  } = getState().discovery.config;
  const allThemes = getState().discovery.themes;

  await localhost
    .post("/docs/discovery/export/csv", {
      folderId,
      docs,
      searchTerm,
      discoveryFilter,
      terms: !showAll ? getCheckedThemes(allThemes) : [],
    })
    .then(async (response) => {
      const jobData = response.data.jobId;
      dispatch(pollJobStatus(jobData));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
      dispatch(clearJobQueue());
    });
};

export const checkUncheckDocument =
  (documentId, ignored) => async (dispatch, getState) => {
    const { isSearch, searchTerm, filterView, filterType, filterDocID } =
      getState().discovery.config;

    if (!ignored) {
      dispatch(removeIgnoredDocument(documentId));
    }

    const docToIgnoreId = ignored ? documentId : null;
    if (filterView) {
      dispatch(applyFilter(filterType, filterDocID, null, docToIgnoreId));
    } else if (isSearch) {
      dispatch(searchItems(searchTerm, docToIgnoreId));
    } else {
      if (ignored) {
        dispatch(addIgnoredDocument(documentId));
      }
      dispatch(setDisableSaveSnapshot(false));
    }
    dispatch(
      fetchUniqueItemsCount(filterType, null, searchTerm, docToIgnoreId)
    );
  };
